import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Trophy, Gamepad2, Crown, Star, Sparkles, Gift, Mail, ChevronDown, ChevronUp } from 'lucide-react';
import BirdAvatar from './BirdAvatar';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ChampionCard = ({ champion, type, delay }) => {
  if (!champion) return null;
  
  const isTopScorer = type === 'scorer';
  
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ delay, duration: 0.4 }}
      className={`
        flex items-center gap-3 px-4 py-3 rounded-xl
        ${isTopScorer 
          ? 'bg-gradient-to-r from-amber-500/20 to-yellow-500/10 border border-amber-500/30' 
          : 'bg-gradient-to-r from-violet-500/20 to-purple-500/10 border border-violet-500/30'
        }
      `}
    >
      {/* Icon */}
      <div className={`
        flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center
        ${isTopScorer ? 'bg-amber-500/30' : 'bg-violet-500/30'}
      `}>
        {isTopScorer ? (
          <Crown className="w-4 h-4 text-amber-400" />
        ) : (
          <Gamepad2 className="w-4 h-4 text-violet-400" />
        )}
      </div>
      
      {/* Avatar */}
      <div className={`
        flex-shrink-0 p-0.5 rounded-full 
        ${isTopScorer 
          ? 'bg-gradient-to-br from-amber-400 to-yellow-500' 
          : 'bg-gradient-to-br from-violet-400 to-purple-500'
        }
      `}>
        <div className="bg-background rounded-full">
          <BirdAvatar avatarId={champion.avatar_id || 1} size={36} />
        </div>
      </div>
      
      {/* Info */}
      <div className="flex-1 min-w-0">
        <p className="font-bold text-sm truncate">
          {champion.nickname}
        </p>
        <p className={`text-xs ${isTopScorer ? 'text-amber-400' : 'text-violet-400'}`}>
          {isTopScorer ? `${champion.score?.toLocaleString()} pts` : `${champion.games_played} games`}
        </p>
      </div>
    </motion.div>
  );
};

const MonthBlock = ({ month, topScorer, mostGames, index }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.15 }}
      className="bg-card/50 backdrop-blur-sm border border-border rounded-2xl p-5 w-full max-w-md"
    >
      {/* Month Header */}
      <div className="text-center mb-4">
        <h4 className="font-heading font-bold text-xl text-gradient-primary">
          {month}
        </h4>
      </div>
      
      {/* Champions - Horizontal Layout */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="flex-1">
          <p className="text-[10px] uppercase tracking-wider text-amber-400 font-semibold mb-2 text-center">
            🏆 Top Scorer
          </p>
          <ChampionCard champion={topScorer} type="scorer" delay={0.2} />
        </div>
        <div className="flex-1">
          <p className="text-[10px] uppercase tracking-wider text-violet-400 font-semibold mb-2 text-center">
            🎮 Most Games
          </p>
          <ChampionCard champion={mostGames} type="games" delay={0.3} />
        </div>
      </div>
    </motion.div>
  );
};

const MonthlyChampions = ({ className = '' }) => {
  const [champions, setChampions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    const fetchChampions = async () => {
      try {
        const response = await fetch(`${API_URL}/api/scores/monthly-champions`);
        if (response.ok) {
          const data = await response.json();
          if (data.success && data.champions) {
            setChampions(data.champions);
          }
        }
      } catch (err) {
        console.error('Error fetching monthly champions:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchChampions();
  }, []);

  // Don't render if no data
  if (loading) {
    return (
      <div className={`py-8 ${className}`}>
        <div className="container mx-auto px-4 text-center">
          <div className="animate-pulse">
            <div className="h-8 bg-muted rounded w-48 mx-auto mb-4"></div>
            <div className="h-4 bg-muted rounded w-64 mx-auto"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error || champions.length === 0) {
    return null;
  }

  return (
    <section className={`py-10 ${className}`}>
      <div className="container mx-auto px-4">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-6"
        >
          <div className="flex items-center justify-center gap-2 mb-2">
            <Star className="w-5 h-5 text-amber-400" />
            <h2 className="text-2xl sm:text-3xl font-heading font-bold">
              Hall of <span className="text-gradient-primary">Champions</span>
            </h2>
            <Star className="w-5 h-5 text-amber-400" />
          </div>
          <p className="text-muted-foreground text-sm">
            Celebrating our monthly legends
          </p>
        </motion.div>

        {/* Champions - Centered Horizontal Layout */}
        <div className="flex flex-wrap justify-center gap-4 mb-6">
          {(showAll ? champions : champions.slice(0, 3)).map((monthData, index) => (
            <MonthBlock
              key={monthData.month}
              month={monthData.month_short}
              topScorer={monthData.top_scorer}
              mostGames={monthData.most_games}
              index={index}
            />
          ))}
        </div>

        {/* Show all / show less toggle */}
        {champions.length > 3 && (
          <div className="flex justify-center mb-6">
            <button
              onClick={() => setShowAll((v) => !v)}
              data-testid="champions-show-all-btn"
              className="inline-flex items-center gap-1.5 px-5 py-2 rounded-full border border-primary/40 bg-primary/10 hover:bg-primary/20 text-sm font-semibold text-primary transition-colors"
            >
              {showAll ? (
                <>Show less <ChevronUp className="w-4 h-4" /></>
              ) : (
                <>Show all {champions.length} months <ChevronDown className="w-4 h-4" /></>
              )}
            </button>
          </div>
        )}

        {/* Prize Note */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.4 }}
          className="text-center"
        >
          <div className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-amber-500/20 via-yellow-500/10 to-amber-500/20 border border-amber-500/30 rounded-full">
            <Gift className="w-4 h-4 text-amber-400" />
            <span className="text-sm font-medium">
              Monthly Champions: email{' '}
              <a 
                href="mailto:support@jailbirdsgame.com?subject=Monthly%20Champion%20Prize%20Claim" 
                className="text-amber-400 hover:text-amber-300 font-bold underline underline-offset-2"
              >
                support@jailbirdsgame.com
              </a>
              {' '}for your prize!
            </span>
            <Sparkles className="w-4 h-4 text-amber-400" />
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default MonthlyChampions;
